import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
/**
 * Australian timezone used for job and estimate Commencement/Completion dates (Sydney = AEST/AEDT, same as Melbourne).
 * All schedule datetimes from the API are displayed in this timezone so estimate and generated job times match.
 */
export const AUS_TIMEZONE = "Australia/Sydney";
/**
 * UI display format for date-only input fields.
 * Note: we still store/submit YYYY-MM-DD; this is purely for display/typing.
 */
export const DATE_INPUT_DISPLAY_FORMAT = "DD-MMM-YYYY";
export function formatDateInputDisplay(value) {
    const dateOnly = toDateOnly(value);
    if (!dateOnly)
        return "";
    const d = dayjs(dateOnly, "YYYY-MM-DD", true);
    if (!d.isValid())
        return "";
    return d.format(DATE_INPUT_DISPLAY_FORMAT);
}
/**
 * Parse a user-entered date string into YYYY-MM-DD (or "" if empty).
 * Accepts:
 * - DD-MMM-YYYY (e.g. 04-Mar-2026)
 * - DD/MM/YYYY
 * - DD-MM-YYYY
 * - YYYY-MM-DD (pasted ISO)
 */
export function parseDateInputDisplay(raw) {
    const s = (raw ?? "").trim();
    if (!s)
        return { value: "", isValid: true };
    const formats = [
        DATE_INPUT_DISPLAY_FORMAT,
        "D-MMM-YYYY",
        "DD/MM/YYYY",
        "D/M/YYYY",
        "DD-MM-YYYY",
        "D-M-YYYY",
        "YYYY-MM-DD",
    ];
    const d = dayjs(s, formats, true);
    if (!d.isValid())
        return { value: "", isValid: false };
    return { value: d.format("YYYY-MM-DD"), isValid: true };
}
/**
 * Parse an ISO datetime string (API returns with timezone or UTC) into date (YYYY-MM-DD) and time (HH:mm)
 * in Australian time for form inputs.
 *
 * Behaviour:
 * - If value includes explicit timezone/UTC marker (Z or +/-HH:mm), parse and convert to AUS_TIMEZONE.
 * - If value has no timezone (e.g. 2026-02-24T08:00:00.0000000), treat it as already in AUS_TIMEZONE
 *   so the time is not double-shifted by the browser's local timezone.
 * - If value is date-only (no "T"), returns date and empty time.
 */
export function parseDateTime(iso) {
    if (iso == null || iso === "") {
        return { date: "", time: "" };
    }
    const s = String(iso).trim();
    const hasExplicitZone = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
    const d = hasExplicitZone
        ? dayjs(iso).tz(AUS_TIMEZONE)
        : dayjs.tz(iso, AUS_TIMEZONE);
    if (!d.isValid()) {
        return { date: s.slice(0, 10), time: "" };
    }
    const date = d.format("YYYY-MM-DD");
    const time = s.includes("T") ? d.format("HH:mm") : "";
    return { date, time };
}
/**
 * Build an ISO datetime string with AUS timezone offset from date (YYYY-MM-DD) and time (HH:mm or empty)
 * interpreted as Australian time. If time is empty, uses "00:00".
 * API expects datetime with timezone (e.g. 2025-02-02T10:00:00+11:00); DB saves as AUS.
 */
export function toDateTime(date, time) {
    if (!date)
        return "";
    const normalizedTime = time && /^\d{1,2}:\d{2}$/.test(time) ? time : "00:00";
    const d = dayjs.tz(`${date}T${normalizedTime}:00`, AUS_TIMEZONE);
    if (!d.isValid()) {
        return `${date}T00:00:00+10:00`;
    }
    return d.format("YYYY-MM-DDTHH:mm:ssZ");
}
/**
 * Extract date-only (YYYY-MM-DD) from a value without timezone conversion.
 * Use for Trade Assignment date display/save only. No parsing as AEST/UTC.
 * - null/undefined/"" → ""
 * - If string contains "T" → first 10 chars (date part as-is)
 * - Otherwise → trimmed string (assume already YYYY-MM-DD)
 */
export function toDateOnly(value) {
    if (value == null || value === "")
        return "";
    const s = value.trim();
    return s.includes("T") ? s.slice(0, 10) : s;
}
/**
 * Format a date value for display (e.g. "2 Feb 2025") without timezone conversion.
 * Uses date part only; no UTC/AEST parsing. Use where date should be shown as-is.
 */
export function formatDateOnlyDisplay(value) {
    const dateOnly = toDateOnly(value);
    if (!dateOnly)
        return "TBD";
    const d = dayjs(dateOnly, "YYYY-MM-DD", true);
    if (!d.isValid())
        return "TBD";
    return d.format("DD-MMM-YYYY");
}
/**
 * Get time (HH:mm) from an ISO datetime string as-is, no timezone conversion.
 * Returns empty string if value is not an ISO string with time part.
 */
export function getTimeFromIso(value) {
    if (value == null || value === "")
        return "";
    const s = value.trim();
    if (!s.includes("T") || s.length < 16)
        return "";
    return s.slice(11, 16);
}
/**
 * Resolve a display time string for UI where time might be stored either:
 * - in the ISO datetime (`dateIso`), or
 * - in a separate field (`timeValue`), or
 * - in a legacy fallback field (`fallback`).
 *
 * Treats empty values and "12:00"/"12:00:00" as placeholders and prefers the time
 * embedded in `dateIso` in that case.
 */
export function resolveDisplayTime(dateIso, timeValue, fallback) {
    const normalized = (timeValue ?? "").trim();
    const useTimeFromDate = !normalized || normalized === "12:00" || normalized === "12:00:00";
    if (useTimeFromDate && dateIso) {
        const time = getTimeFromIso(dateIso);
        if (time)
            return time;
    }
    return normalized || (fallback ?? "").trim() || "";
}
/**
 * Format datetime for display without timezone conversion (date and time as stored).
 * Use for trade date change requests where values must not be converted to AUT/UTC.
 */
export function formatDateTimeAsIs(value) {
    if (value == null || value === "")
        return "—";
    const dateOnly = toDateOnly(value);
    const time = getTimeFromIso(value);
    if (!dateOnly)
        return "—";
    const d = dayjs(dateOnly, "YYYY-MM-DD", true);
    if (!d.isValid())
        return value.slice(0, 19) || "—";
    const datePart = d.format("DD-MMM-YYYY");
    if (time)
        return `${datePart} ${time}`;
    return datePart;
}
/**
 * Build a datetime string from date and time without timezone (as-is, no AUT/UTC conversion).
 * Use when persisting Commencement/Completion so the stored value matches user input.
 * - Input: dateStr (YYYY-MM-DD), timeStr (HH:mm or empty)
 * - Output: YYYY-MM-DDTHH:mm:ss (no Z, no offset)
 */
export function toDateTimeLocal(dateStr, timeStr) {
    const date = toDateOnly(dateStr);
    if (!date)
        return "";
    const normalizedTime = timeStr && /^\d{1,2}:\d{2}$/.test(timeStr.trim()) ? timeStr.trim() : "00:00";
    return `${date}T${normalizedTime}:00`;
}
/**
 * Format an ISO datetime string for display in Australian time (e.g. "DD-MMM-YYYY HH:mm").
 * Accepts both UTC (Z) and offset (e.g. +11:00). Use in modals or labels where job dates are shown.
 */
export function formatDateTimeAUS(iso) {
    if (iso == null || iso === "")
        return "—";
    const d = dayjs(iso).tz(AUS_TIMEZONE);
    if (!d.isValid())
        return iso.slice(0, 19) || "—";
    return d.format("DD-MMM-YYYY HH:mm");
}
/**
 * Format an ISO datetime string as date-only in Australian time (e.g. "25-Feb-2026").
 * Uses the same parsing rule as parseDateTime: values without timezone are interpreted as Australian time
 * so the calendar date is correct for AEDT. Use for Building Permit / CFI upload date display.
 */
export function formatDateOnlyAUS(iso) {
    if (iso == null || iso === "")
        return "—";
    const s = String(iso).trim();
    const hasExplicitZone = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
    const d = hasExplicitZone
        ? dayjs(iso).tz(AUS_TIMEZONE)
        : dayjs.tz(iso, AUS_TIMEZONE);
    if (!d.isValid())
        return s.slice(0, 19) || "—";
    return d.format("DD-MMM-YYYY");
}
/**
 * Parse an ISO datetime string, treating strings without timezone as UTC.
 * Fixes "7h ago" etc. when API returns e.g. "2025-02-23T10:00:00" without Z.
 */
export function parseUtcIso(iso) {
    if (iso == null || iso === "")
        return new Date(NaN);
    const s = String(iso).trim();
    if (s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s))
        return new Date(s);
    return new Date(s + "Z");
}
/**
 * Format an ISO datetime as relative time for notification lists (e.g. "Now", "5m ago", "2h ago", "1d ago").
 * Treats strings without timezone (no Z or offset) as UTC so "X ago" is correct when API omits timezone.
 */
export function formatRelativeTime(iso) {
    if (iso == null || iso === "")
        return "—";
    const d = dayjs(parseUtcIso(iso));
    if (!d.isValid())
        return "—";
    const diffMinutes = dayjs().diff(d, "minute");
    if (diffMinutes < 1)
        return "Now";
    if (diffMinutes < 60)
        return `${diffMinutes}m ago`;
    const diffHours = dayjs().diff(d, "hour");
    if (diffHours < 24)
        return `${diffHours}h ago`;
    const diffDays = dayjs().diff(d, "day");
    if (diffDays < 7)
        return `${diffDays}d ago`;
    return d.tz(AUS_TIMEZONE).format("DD-MMM-YYYY");
}
/**
 * Current date/time in Australian timezone (AEST/AEDT) as ISO string (e.g. 2025-02-19T14:30:00+11:00).
 * Use when persisting "now" for job permit/CFI date fields so the stored value is in AUS time.
 */
export function nowAUSISO() {
    return dayjs().tz(AUS_TIMEZONE).format();
}
/**
 * Returns today's date in Australia/Sydney as YYYY-MM-DD.
 * Used as "change status date" when computing delay on status change to Completed.
 */
export function getTodayAUS() {
    return dayjs().tz(AUS_TIMEZONE).format("YYYY-MM-DD");
}
/**
 * Compute delay in whole days as max(0, changeStatusDate - completionDate).
 * Both args are YYYY-MM-DD. Returns 0 if either date is missing or invalid.
 * Used when job status is set to Completed to auto-fill Delay Number (days late).
 */
export function getDelayDays(completionDate, changeStatusDate) {
    if (!completionDate?.trim() || !changeStatusDate?.trim())
        return 0;
    const comp = dayjs(completionDate, "YYYY-MM-DD", true);
    const change = dayjs(changeStatusDate, "YYYY-MM-DD", true);
    if (!comp.isValid() || !change.isValid())
        return 0;
    const diff = change.diff(comp, "day");
    return Math.max(0, diff);
}
/**
 * Returns true if the given date (YYYY-MM-DD) is a Saturday or Sunday.
 * Used to block weekend selection for Job Commencement Date.
 * Empty or invalid date returns false (allows clearing the field).
 */
export function isWeekend(date) {
    if (!date || date.trim() === "")
        return false;
    const d = dayjs(date, "YYYY-MM-DD", true);
    if (!d.isValid())
        return false;
    const day = d.day();
    return day === 0 || day === 6;
}
